interface FooterProps {
  isDarkMode?: boolean;
}

export default function Footer({ isDarkMode = false }: FooterProps) {
  const quickLinks = [
    { label: '關於我們', href: '#about' },
    { label: '賽事活動', href: '#events' },
    { label: '培訓課程', href: '#training' },
    { label: '最新消息', href: '#news' },
    { label: '相簿', href: '#gallery' },
    { label: '會員專區', href: '#members' }
  ];

  const contactInfo = [
    { icon: 'ri-phone-line', text: '+852 65003988' },
    { icon: 'ri-mail-line', text: 'pp83838@gmail.com' },
    { icon: 'ri-map-pin-line', text: '新界上水郵政信箱778號' },
    { icon: 'ri-time-line', text: '週一至週五 09:00-18:00' }
  ];

  const socialMedia = [
    { icon: 'ri-facebook-fill', label: 'Facebook', href: 'https://www.facebook.com/chang.shing.58' },
    { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
    { icon: 'ri-youtube-fill', label: 'YouTube', href: '#' },
    { icon: 'ri-twitter-x-line', label: 'X (Twitter)', href: '#' },
    { icon: 'ri-wechat-line', label: 'WeChat', href: '#' },
    { icon: 'ri-whatsapp-line', label: 'WhatsApp', href: 'https://wa.me/85265003988' }
  ];

  return (
    <footer id="contact" className={`${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white'} pt-16 pb-8`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Logo and Description */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img
                src="https://public.readdy.ai/ai/img_res/a52d9b2d-d6bf-4de3-8d49-31037e97823f.png"
                alt="大灣乒乓聯合會"
                className="h-16 w-16 object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <div>
                <div className="text-xl font-bold">大灣乒乓聯合會</div>
                <div className="text-sm text-gray-400">GBTTA</div>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              致力於推廣乒乓球運動，培養優秀運動員，促進大灣區體育交流與發展。
            </p>
            <div className="flex items-center space-x-3">
              {socialMedia.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-emerald-600 rounded-lg transition-all hover:scale-110 cursor-pointer"
                >
                  <i className={`${social.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6">快速連結</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-emerald-400 transition-colors flex items-center cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line mr-2"></i>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-6">聯絡資訊</h3>
            <ul className="space-y-4">
              {contactInfo.map((info, index) => (
                <li key={index} className="flex items-start text-gray-400">
                  <div className="w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                    <i className={`${info.icon} text-emerald-400`}></i>
                  </div>
                  <span className="leading-relaxed">{info.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-xl font-bold mb-6">訂閱電子報</h3>
            <p className="text-gray-400 mb-4">
              訂閱我們的電子報，獲取最新賽事資訊和活動通知
            </p>
            <form className="space-y-3">
              <input
                type="email"
                placeholder="您的電郵地址"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-white placeholder-gray-400"
              />
              <button
                type="submit"
                className="w-full px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-send-line mr-2"></i>
                訂閱
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col items-center space-y-4">
            <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight drop-shadow-2xl">
              大灣乒乓聯合會
            </h1>
            <div className="flex flex-col md:flex-row justify-between items-center w-full space-y-4 md:space-y-0 mt-4">
              <div className="text-gray-400 text-sm text-center md:text-left">
                © 2024 大灣乒乓聯合會 Greater Bay Table Tennis Association. All rights reserved.
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <a href="#privacy" className="text-gray-400 hover:text-emerald-400 transition-colors cursor-pointer">
                  私隱政策
                </a>
                <a href="#terms" className="text-gray-400 hover:text-emerald-400 transition-colors cursor-pointer">
                  使用條款
                </a>
                <a 
                  href="https://readdy.ai/?ref=logo" 
                  target="_blank" 
                  rel="nofollow noopener noreferrer" 
                  className="text-gray-400 hover:text-emerald-400 transition-colors cursor-pointer"
                >
                  Website Builder
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
